import { useState } from 'react';
import type { EmployeeDetailDto, EmployeeDto, PagedResult } from '@shared/types';
import { initials, localDate, localDateTime, money, titleCase } from '@shared/format';
import { errorMessage, useApp, useLoad } from '../context';
import { navigate } from '../router';
import { Card, Empty, ErrorBox, Loading, Modal, Pill, TopBar } from '../components/ui';

export function Employees() {
  const { api } = useApp();
  const [search, setSearch] = useState('');
  const [inactive, setInactive] = useState(false);
  const { data, error, loading, reload } = useLoad(
    () => api.get<PagedResult<EmployeeDto>>(`/api/employees?take=500&includeInactive=${inactive}${search ? `&search=${encodeURIComponent(search)}` : ''}`),
    [api, search, inactive]);
  return (
    <div className="page">
      <TopBar title="Employees" />
      <div className="search-wrap"><input className="input" type="search" placeholder="Search name, email, ID…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search employees" /></div>
      <label className="check-line"><input type="checkbox" checked={inactive} onChange={(e) => setInactive(e.target.checked)} /> Include inactive</label>
      {loading && !data && <Loading />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && (
        <Card>
          <div className="muted small">{data.total} employee{data.total === 1 ? '' : 's'}</div>
          {data.items.length === 0 ? <Empty>No employees. Run a Connecteam employee sync from Integration.</Empty> : data.items.map((e) => (
            <button key={e.employeeId} className="list-row" onClick={() => navigate(`/employees/${e.employeeId}`)}>
              <span className="avatar-sm">{initials(e.displayName)}</span>
              <div className="grow">
                <strong>{e.displayName}</strong> {!e.active && <Pill kind="muted">Inactive</Pill>} {e.hideFromLeaderboard && <Pill kind="warn">Hidden</Pill>}
                <div className="muted small">{[e.employeeNumber && `ID ${e.employeeNumber}`, e.department, e.jobTitle].filter(Boolean).join(' • ')}</div>
              </div>
              <span className="chev">›</span>
            </button>
          ))}
        </Card>
      )}
    </div>
  );
}

export function EmployeeDetail({ employeeId }: { employeeId: string }) {
  const { api, toast } = useApp();
  const { data, error, loading, reload } = useLoad(() => api.get<EmployeeDetailDto>(`/api/employees/${employeeId}`), [api, employeeId]);
  const [tab, setTab] = useState<'activity' | 'wins'>('activity');
  const [adjust, setAdjust] = useState<null | { amount: string; reason: string; corrects?: string }>(null);
  const [busy, setBusy] = useState(false);

  const submitAdjust = async () => {
    if (!adjust) return;
    setBusy(true);
    try {
      await api.post('/api/tokens/adjustments', {
        employeeId, tokenAmount: Number(adjust.amount), reason: adjust.reason.trim(), correctsTransactionId: adjust.corrects ?? null,
        kind: adjust.corrects ? 'correction' : Number(adjust.amount) > 0 ? 'manual' : 'correction',
      });
      toast('Adjustment recorded'); setAdjust(null); reload();
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  const toggleHidden = async () => {
    if (!data) return;
    try { await api.patch(`/api/employees/${employeeId}/display`, { hideFromLeaderboard: !data.employee.hideFromLeaderboard }); reload(); }
    catch (e) { toast(errorMessage(e), 'error'); }
  };

  if (loading && !data) return <div className="page"><TopBar title="Employee" back="/employees" /><Loading /></div>;
  if (error || !data) return <div className="page"><TopBar title="Employee" back="/employees" /><ErrorBox message={error ?? 'Not found'} onRetry={reload} /></div>;
  const e = data.employee;
  const adjAmount = Number(adjust?.amount);
  const adjValid = !!adjust && Number.isInteger(adjAmount * 4) && adjAmount !== 0 && Math.abs(adjAmount) <= 10000 && adjust.reason.trim().length >= 3;

  return (
    <div className="page">
      <TopBar title="Employee Profile" back="/employees" />
      <Card>
        <div className="profile">
          <span className="avatar-lg">{initials(e.displayName)}</span>
          <div>
            <h2>{e.displayName}</h2>
            <div className="muted small">Employee ID: {e.employeeNumber ?? '—'}</div>
            <div className="muted small">Department: {e.department ?? '—'}{e.jobTitle ? ` • ${e.jobTitle}` : ''}</div>
            <div className="small">Status: {e.active ? <span className="ok-text">Active</span> : <span className="muted">Inactive</span>}</div>
          </div>
        </div>
        <div className="totals">
          <div><strong className="tabular">{data.totals.weekly}</strong><span>This Week</span></div>
          <div><strong className="tabular">{data.totals.monthly}</strong><span>This Month</span></div>
          <div><strong className="tabular">{data.totals.quarterly}</strong><span>This Quarter</span></div>
          <div><strong className="tabular">{data.totals.annual.toLocaleString()}</strong><span>This Year</span></div>
        </div>
        <div className="row-gap mt">
          {e.active && <button className="btn btn-go btn-sm" onClick={() => navigate(`/award/${e.employeeId}`)}>Award points</button>}
          <button className="btn btn-secondary btn-sm" onClick={() => setAdjust({ amount: '', reason: '' })}>Adjust points</button>
          <button className="btn btn-secondary btn-sm" onClick={toggleHidden}>{e.hideFromLeaderboard ? 'Show on leaderboard' : 'Hide from leaderboard'}</button>
        </div>
      </Card>
      <div className="tabs">
        <button className={tab === 'activity' ? 'on' : ''} onClick={() => setTab('activity')}>Recent Activity</button>
        <button className={tab === 'wins' ? 'on' : ''} onClick={() => setTab('wins')}>Drawing History</button>
      </div>
      <Card className="table-card">
        {tab === 'activity' ? (data.recentTransactions.length === 0 ? <Empty>No point activity yet.</Empty> : (
          <table className="table">
            <thead><tr><th>Date</th><th>Activity</th><th className="right">Points</th><th /></tr></thead>
            <tbody>{data.recentTransactions.map((t) => (
              <tr key={t.transactionId}>
                <td title={localDateTime(t.occurredAtUtc)}>{localDate(t.occurredAtUtc)}</td>
                <td>{t.sourceFormName ?? t.description}<div className="muted small">{titleCase(t.transactionType.replace(/([a-z])([A-Z])/g, '$1 $2'))}{t.transactionType !== 'FormAward' ? ` • ${t.createdBy}` : ''}</div></td>
                <td className={`right tabular ${t.tokenAmount < 0 ? 'neg' : 'pos'}`}>{t.tokenAmount > 0 ? '+' : ''}{t.tokenAmount}</td>
                <td>{t.tokenAmount > 0 && <button className="btn-link small" onClick={() => setAdjust({ amount: String(-t.tokenAmount), reason: '', corrects: t.transactionId })}>Correct</button>}</td>
              </tr>))}
            </tbody>
          </table>
        )) : (data.wins.length === 0 ? <Empty>No drawing wins yet.</Empty> : (
          <table className="table">
            <thead><tr><th>Date</th><th>Drawing</th><th>Prize</th><th className="right">Value</th></tr></thead>
            <tbody>{data.wins.map((w) => (
              <tr key={w.drawingId}><td>{localDate(w.completedAtUtc)}</td><td>{titleCase(w.drawingType)}</td><td>{w.prizeName}</td><td className="right tabular">{money(w.prizeValue)}</td></tr>))}
            </tbody>
          </table>
        ))}
      </Card>
      {adjust && (
        <Modal title={adjust.corrects ? 'Correct a transaction' : 'Adjust points'} onClose={() => setAdjust(null)} actions={<>
          <button className="btn btn-secondary" onClick={() => setAdjust(null)}>Cancel</button>
          <button className="btn btn-primary" disabled={!adjValid || busy} onClick={submitAdjust}>{busy ? 'Saving…' : 'Record adjustment'}</button>
        </>}>
          <p className="muted small">Point history is never edited. This posts a new, audited ledger entry.</p>
          <label className="field"><span>Points (+ to award, − to remove; steps of 0.25)</span>
            <input className="input tabular" inputMode="decimal" placeholder="e.g. 0.25, 1, -0.5" value={adjust.amount} onChange={(ev) => setAdjust({ ...adjust, amount: ev.target.value.replace(/[^\d.-]/g, '') })} /></label>
          <label className="field"><span>Reason (required)</span>
            <textarea className="input" rows={3} maxLength={500} value={adjust.reason} onChange={(ev) => setAdjust({ ...adjust, reason: ev.target.value })} /></label>
        </Modal>
      )}
    </div>
  );
}
